import { addCameraShake } from '../engine/camera.js';
import { damageEnemy } from '../entities/enemies.js';
import { triggerDeath, triggerHurt } from '../entities/player_anim.js';
import { grantKillRewards } from './progression.js';
import { damagePlayer, game } from './state.js';
import { recordWavedashKill } from '../platform/wavedash.js';

let combat = null;

export function initCombat(deps = {}) {
  combat = deps;
  return combat;
}

export function handleBulletHit(enemy, bullet) {
  const weapon = combat.currentWeapon();
  let killed = damageEnemy(enemy, bullet.damage, {
    onKill: (dead) => completeEnemyKill(dead, { ...weapon, source: 'weapon' }),
  });
  if (!killed && bullet.execute && enemy.hp / Math.max(1, enemy.maxHp) <= 0.18 + bullet.execute * 0.035) {
    killed = damageEnemy(enemy, enemy.hp + 1, {
      onKill: (dead) => completeEnemyKill(dead, { ...weapon, source: 'execute' }),
    });
  }

  const type = killed || bullet.crit ? 'crit' : bullet.explode || bullet.freeze ? 'freeze' : 'normal';
  if (!killed && (bullet.crit || bullet.freeze)) {
    combat.spawnDamageNumber(enemy.pos, null, type, combat.camera, {
      killed: false,
      crit: bullet.crit,
      text: bullet.crit ? 'CRIT' : 'SLOW',
    });
  }
  addCameraShake(killed ? 0.3 : bullet.crit ? 0.14 : 0.055, game.shakeEnabled);
  combat.playImpact(killed ? 'kill' : enemy.boss ? 'boss' : 'hit', enemy.pos);
  combat.rumbleGamepad(55, 0.18, killed ? 0.55 : 0.28);
  combat.spawnParticles(enemy.pos, bullet.color, 3, 0.75, { type: 'sparks' });
  combat.spawn('bloodSpray', enemy.pos, { color: bullet.color || 0xff4f68, count: 6, power: 0.8 });
  combat.spawnDecal(bullet.pos, null, 'blood', { size: 0.35 });
  combat.spawnExplosionRing?.(enemy.pos, { color: bullet.color || 0x53ffb5, maxScale: killed ? 1.9 : 0.85, ttl: killed ? 0.34 : 0.16, opacity: killed ? 0.78 : 0.42 });
  if (killed) {
    combat.spawnBeam?.(enemy.pos, { color: bullet.color || 0xff70d7, radius: enemy.boss ? 0.52 : 0.2, ttl: 0.18, height: enemy.boss ? 18 : 8, light: enemy.boss ? 8 : 3.4 });
    combat.spawn('debris', enemy.pos, { color: bullet.color || 0xff70d7, count: enemy.boss ? 28 : 12, power: enemy.boss ? 1.8 : 1.05 });
  }
  if (bullet.ricochet > 0) combat.spawnLightningArc?.(bullet.pos, enemy.pos, { color: bullet.color, ttl: 0.12, jitter: 0.24 });
  if (bullet.explode) spawnExplosion(bullet.pos, bullet.color, bullet.damage * 0.85, 3.8 + bullet.explode * 0.65);
  if (bullet.freeze) enemy.slowTimer = Math.max(enemy.slowTimer || 0, 1.1 + bullet.freeze);
  if (bullet.stagger) staggerEnemy(enemy, bullet);
  if (game.upgrades.chainLightning) zapNearby(enemy, bullet.damage * 0.45, 1 + game.upgrades.chainLightning);
}

export function completeEnemyKill(dead, source = {}) {
  grantKillRewards(game, dead);
  const killSource = normalizeKillSource(source);
  combat.pushKillFeed(killSource, dead);
  combat.spawnDamageNumber(dead.pos, game.comboKills, 'crit', combat.camera, {
    killed: true,
    text: `Kill ${game.comboKills}`,
  });
  combat.playImpact('kill', dead.pos);
  combat.rumbleGamepad(55, 0.18, dead.boss ? 0.7 : 0.48);
  recordWavedashKill(game, dead, killSource);
  triggerKillPowers(dead);
}

export function handleBulletExpire(bullet) {
  if (bullet.explode) spawnExplosion(bullet.pos, bullet.color, bullet.damage * 0.65, 3.4 + bullet.explode * 0.55);
}

export function handleEnemyContact(enemy, damage, player) {
  const shield = game.upgrades.barrier ? Math.min(0.45, game.upgrades.barrier * 0.12) : 0;
  damagePlayer(damage * (1 - shield));
  triggerHurt();
  if (game.health <= 0) triggerDeath();
  combat.updateDamageFx(game.health, game.maxHealth);
  combat.flashScreen('rgba(255,49,86,0.62)', 0.2, 90);
  combat.spawnDamageNumber(player.pos, null, 'self', combat.camera, { text: 'HURT' });
  addCameraShake(0.72, game.shakeEnabled);
  combat.playImpact('hurt', player.pos);
  combat.rumbleGamepad(80, 0.3, 0.65);
  repel(enemy, player);
}

export function handleEnemyShotHit(shot, player) {
  damagePlayer(shot.damage);
  triggerHurt();
  if (game.health <= 0) triggerDeath();
  combat.updateDamageFx(game.health, game.maxHealth);
  combat.flashScreen('rgba(255,49,86,0.48)', 0.16, 80);
  combat.spawnDamageNumber(player.pos, null, 'self', combat.camera, { text: 'HURT' });
  addCameraShake(0.58, game.shakeEnabled);
  combat.playImpact('hurt', player.pos);
}

export function updatePowers(dt, { player, enemies }) {
  const u = game.upgrades || {};
  if (u.regen) game.health = Math.min(game.maxHealth, game.health + dt * u.regen * 0.8);
  if (u.freezeAura) freezeAura(enemies, player, u.freezeAura);
  if (u.skyLaser) tickSkyLaser(dt, enemies, u.skyLaser);
  if (u.stormCall) tickStorm(dt, enemies, u.stormCall);
  if (u.bladeAura) tickBladeAura(dt, enemies, player, u.bladeAura);
  if (u.blackHole) tickBlackHole(dt, enemies, u.blackHole);
  if (u.meteorRain) tickMeteorRain(dt, enemies, u.meteorRain);
  if (u.shockWeb) tickShockWeb(dt, enemies, u.shockWeb);
}

function triggerKillPowers(dead) {
  const u = game.upgrades || {};
  if (u.vampire) game.health = Math.min(game.maxHealth, game.health + 1.4 * u.vampire);
  if (u.deathNova) {
    combat.spawn('shockwave', dead.pos, { color: 0xff70d7, count: 1, power: 1.6, ttl: 0.42 });
    combat.spawnBeam?.(dead.pos, { color: 0xff70d7, radius: 0.24, ttl: 0.22, light: 4.5 });
    combat.spawnExplosionRing?.(dead.pos, { color: 0xff70d7, maxScale: 3.8 + u.deathNova * 0.6, ttl: 0.42 });
    for (const enemy of combat.getEnemies()) {
      if (!enemy.alive || enemy === dead) continue;
      if (enemy.pos.distanceToSquared(dead.pos) < (4 + u.deathNova) ** 2) {
        damageEnemy(enemy, 0.7 + u.deathNova * 0.45, { onKill: passiveKill('deathNova', 'Death Nova') });
      }
    }
  }
}

function spawnExplosion(position, color, damage = 1.2, radius = 4.2) {
  combat.spawn('shockwave', position, { color, count: 1, power: 2.4, ttl: 0.52, y: 0.08 });
  combat.spawnExplosionRing?.(position, { color: color || 0xffd166, maxScale: 4.8, ttl: 0.46 });
  combat.spawn('debris', position, { color, count: 18, power: 1.3 });
  combat.spawn('smoke', position, { color: 0x87919e, count: 8, power: 1.2 });
  combat.spawnDecal(position, null, 'scorch', { size: 1.2, opacity: 0.62 });
  addCameraShake(0.45, game.shakeEnabled);
  combat.flashScreen('rgba(255,209,102,0.42)', 0.18, 90);
  for (const enemy of combat.getEnemies()) {
    if (!enemy.alive || enemy.pos.distanceToSquared(position) > radius * radius) continue;
    damageEnemy(enemy, damage, { onKill: passiveKill('explode', 'Blast Rounds') });
    enemy.slowTimer = Math.max(enemy.slowTimer || 0, 0.18);
  }
}

function repel(enemy, player) {
  const dx = enemy.pos.x - player.pos.x;
  const dz = enemy.pos.z - player.pos.z;
  const len = Math.hypot(dx, dz) || 1;
  const min = enemy.radius + player.radius + 0.45;
  enemy.pos.x = player.pos.x + (dx / len) * min;
  enemy.pos.z = player.pos.z + (dz / len) * min;
  enemy.vel.x = (dx / len) * Math.max(5, enemy.speed * 0.75);
  enemy.vel.z = (dz / len) * Math.max(5, enemy.speed * 0.75);
  enemy.group.position.copy(enemy.pos);
}

function staggerEnemy(enemy, bullet) {
  const strength = Math.min(2.8, 0.45 + bullet.stagger * 0.32);
  const dx = bullet.vel.x;
  const dz = bullet.vel.z;
  const len = Math.hypot(dx, dz) || 1;
  enemy.pos.x += (dx / len) * strength;
  enemy.pos.z += (dz / len) * strength;
  enemy.vel.x += (dx / len) * strength * 2.2;
  enemy.vel.z += (dz / len) * strength * 2.2;
  enemy.slowTimer = Math.max(enemy.slowTimer || 0, 0.18 + bullet.stagger * 0.08);
}

function freezeAura(enemies, player, stacks) {
  const r2 = (4.2 + stacks * 0.9) ** 2;
  for (const enemy of enemies) {
    if (enemy.alive && enemy.pos.distanceToSquared(player.pos) < r2) {
      enemy.slowTimer = Math.max(enemy.slowTimer || 0, 0.35);
      if (Math.random() < 0.08) combat.spawn('sparks', enemy.pos, { color: 0x9cfffa, count: 2, power: 0.55 });
    }
  }
}

function tickSkyLaser(dt, enemies, stacks) {
  tickSkyLaser.t = (tickSkyLaser.t || 0) - dt;
  if (tickSkyLaser.t > 0) return;
  tickSkyLaser.t = Math.max(1.6, 4.2 - stacks * 0.35);
  const target = selectClusterTarget(enemies);
  if (!target) return;
  combat.spawnBeam?.(target.pos, { color: 0x6aa9ff, radius: 0.64 + stacks * 0.08, ttl: 0.42, light: 10 });
  combat.spawnExplosionRing?.(target.pos, { color: 0x6aa9ff, maxScale: 4.2 + stacks * 0.45, ttl: 0.42 });
  combat.spawn('shockwave', target.pos, { color: 0x6aa9ff, count: 1, power: 2.8, ttl: 0.42, y: 0.12 });
  addCameraShake(0.28, game.shakeEnabled);
  combat.flashScreen('rgba(106,169,255,0.22)', 0.1, 70);
  for (const enemy of enemies) {
    if (enemy.alive && enemy.pos.distanceToSquared(target.pos) < (3.6 + stacks * 0.5) ** 2) {
      damageEnemy(enemy, 3.1 + stacks * 1.25, { onKill: passiveKill('skyLaser', 'Judgement Beam') });
    }
  }
}

function tickStorm(dt, enemies, stacks) {
  tickStorm.t = (tickStorm.t || 0) - dt;
  if (tickStorm.t > 0) return;
  tickStorm.t = Math.max(0.5, 1.6 - stacks * 0.12);
  const target = enemies.find((enemy) => enemy.alive);
  if (!target) return;
  combat.spawnBeam?.(target.pos, { color: 0xb044ff, radius: 0.22, ttl: 0.2, light: 5 });
  combat.spawnLightningArc?.({ x: target.pos.x, y: 10, z: target.pos.z - 2 }, target.pos, { color: 0xb044ff, ttl: 0.2, height: 1.4, arcHeight: 1.8 });
  damageEnemy(target, 1.35 + stacks * 0.62, { onKill: passiveKill('stormCall', 'Storm Call') });
  combat.spawn('sparks', target.pos, { color: 0x6aa9ff, count: 8, power: 1.1 });
}

function tickBladeAura(dt, enemies, player, stacks) {
  tickBladeAura.t = (tickBladeAura.t || 0) - dt;
  if (tickBladeAura.t > 0) return;
  tickBladeAura.t = Math.max(0.12, 0.28 - stacks * 0.025);
  const radius = 3.2 + stacks * 0.42;
  const damage = 0.75 + stacks * 0.36;
  combat.spawn('sparks', player.pos, { color: 0xff70d7, count: 5 + stacks, power: 0.58, ttl: 0.22, y: 1.05 });
  for (const enemy of enemies) {
    if (!enemy.alive || enemy.pos.distanceToSquared(player.pos) > radius * radius) continue;
    damageEnemy(enemy, damage, { onKill: passiveKill('bladeAura', 'Shredder Halo') });
    enemy.slowTimer = Math.max(enemy.slowTimer || 0, 0.16);
  }
}

function tickBlackHole(dt, enemies, stacks) {
  tickBlackHole.t = (tickBlackHole.t || 0) - dt;
  if (tickBlackHole.t > 0) return;
  tickBlackHole.t = Math.max(2.8, 7.2 - stacks * 0.55);
  const target = selectClusterTarget(enemies);
  if (!target) return;

  const radius = 5.4 + stacks * 0.72;
  combat.spawn('shockwave', target.pos, { color: 0x12051f, count: 1, power: 4.2 + stacks * 0.45, ttl: 0.82, y: 0.1 });
  combat.spawnExplosionRing?.(target.pos, { color: 0xb044ff, maxScale: 5.4 + stacks, ttl: 0.78 });
  combat.spawn('sparks', target.pos, { color: 0xb044ff, count: 22, power: 1.8 });
  combat.spawnBeam?.(target.pos, { color: 0xb044ff, radius: 0.42, ttl: 0.54, light: 7 });
  addCameraShake(0.34, game.shakeEnabled);

  for (const enemy of enemies) {
    if (!enemy.alive || enemy.pos.distanceToSquared(target.pos) > radius * radius) continue;
    const dx = target.pos.x - enemy.pos.x;
    const dz = target.pos.z - enemy.pos.z;
    enemy.pos.x += dx * 0.18;
    enemy.pos.z += dz * 0.18;
    damageEnemy(enemy, 2.2 + stacks * 1.1, { onKill: passiveKill('blackHole', 'Singularity Mine') });
  }
}

function tickMeteorRain(dt, enemies, stacks) {
  tickMeteorRain.t = (tickMeteorRain.t || 0) - dt;
  if (tickMeteorRain.t > 0) return;
  tickMeteorRain.t = Math.max(1.9, 5.4 - stacks * 0.38);
  const targets = [...enemies]
    .filter((enemy) => enemy.alive)
    .sort((a, b) => (b.boss ? 3 : b.elite ? 2 : 1) - (a.boss ? 3 : a.elite ? 2 : 1))
    .slice(0, Math.min(2 + stacks, 5));
  for (const target of targets) {
    combat.spawnBeam?.(target.pos, { color: 0xffd166, radius: 0.32, ttl: 0.24, light: 5.8 });
    combat.spawnExplosionRing?.(target.pos, { color: 0xffd166, maxScale: 2.7, ttl: 0.28 });
    combat.spawn('debris', target.pos, { color: 0xffd166, count: 10, power: 1.25 });
    combat.spawn('shockwave', target.pos, { color: 0xff4f68, count: 1, power: 1.5, ttl: 0.32, y: 0.08 });
    damageEnemy(target, 2.7 + stacks * 1.05, { onKill: passiveKill('meteorRain', 'Orbital Rain') });
  }
}

function tickShockWeb(dt, enemies, stacks) {
  tickShockWeb.t = (tickShockWeb.t || 0) - dt;
  if (tickShockWeb.t > 0) return;
  tickShockWeb.t = Math.max(0.38, 0.92 - stacks * 0.07);
  let arcs = 0;
  for (const source of enemies) {
    if (!source.alive) continue;
    const target = enemies.find((enemy) => (
      enemy.alive
      && enemy !== source
      && enemy.pos.distanceToSquared(source.pos) < (5.2 + stacks * 0.45) ** 2
    ));
    if (!target) continue;
    combat.spawnLightningArc?.(source.pos, target.pos, { color: 0x9cfffa, ttl: 0.18, jitter: 0.42 });
    combat.spawnBeam?.(target.pos, { color: 0x9cfffa, radius: 0.12, ttl: 0.15, light: 2.6 });
    combat.spawn('sparks', target.pos, { color: 0x9cfffa, count: 4, power: 0.8 });
    damageEnemy(target, 0.85 + stacks * 0.42, { onKill: passiveKill('shockWeb', 'Voltage Web') });
    arcs += 1;
    if (arcs >= 2 + stacks) break;
  }
}

function zapNearby(source, damage, jumps) {
  let origin = source;
  for (let i = 0; i < jumps; i += 1) {
    const target = combat.getEnemies().find((enemy) => enemy.alive && enemy !== origin && enemy.pos.distanceToSquared(origin.pos) < 64);
    if (!target) break;
    combat.spawnLightningArc?.(origin.pos, target.pos, { color: 0x53ffb5, ttl: 0.18, jitter: 0.48 });
    damageEnemy(target, damage, { onKill: passiveKill('chainLightning', 'Chain Spark') });
    combat.spawnBeam?.(target.pos, { color: 0x53ffb5, radius: 0.15, ttl: 0.14, light: 3.2 });
    combat.spawn('sparks', target.pos, { color: 0x6aa9ff, count: 5, power: 0.9 });
    origin = target;
  }
}

function selectClusterTarget(enemies) {
  let best = null;
  let bestScore = -1;
  for (const candidate of enemies) {
    if (!candidate.alive) continue;
    let score = candidate.boss ? 6 : candidate.elite ? 3 : 1;
    for (const enemy of enemies) {
      if (enemy.alive && enemy !== candidate && enemy.pos.distanceToSquared(candidate.pos) < 36) score += 1;
    }
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

function passiveKill(id, name) {
  return (dead) => completeEnemyKill(dead, { id, name, source: 'passive' });
}

function normalizeKillSource(source = {}) {
  return {
    id: source.id || source.source || 'passive',
    name: source.name || source.label || 'Passive',
    source: source.source || 'passive',
  };
}
